import { Box, CircularProgress, InputBase, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  AlertTriangle,
  ArrowUp,
  Check,
  ChevronRight,
  Copy,
  DollarSign,
  History,
  Lock,
  Package,
  Plus,
  RotateCcw,
  Sparkles,
  Square,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import { KitButton, KitItem } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { ProtoIcon, protoIcon } from '../common/ProtoIcon';
import { useAppBarAction, useAppBarSubtitle } from '../layout/AppBarActions';
import { deleteConversation, listConversations } from '../../services/aiChat';
import { aiChatOpenState } from './aiChatState';
import { rotuloDaTool, sugestoesPorTools } from './deepLinks';
import MessageContent, { DeepLinkChip } from './MessageContent';
import { useAiChat } from './useAiChat';

const RefreshIcon = protoIcon('refresh');
const HistoryIcon = protoIcon('history');

const MOBILE_BREAKPOINT = '(max-width:640px)';
const MAX_MENSAGEM = 4000; // espelha maxRunesMensagem do fv-ai

const SUGESTOES = [
  { id: 'vendas', titulo: 'Vendas de hoje', detalhe: 'Resumo e comparação com ontem', pergunta: 'Como estão as vendas de hoje?', Icon: DollarSign, tom: 'info' },
  { id: 'estoque', titulo: 'Produtos com estoque baixo', detalhe: 'Abaixo do mínimo cadastrado', pergunta: 'Quais produtos estão com estoque baixo?', Icon: Package, tom: 'warning' },
  { id: 'clientes', titulo: 'Maiores clientes do mês', detalhe: 'Ranking por valor comprado', pergunta: 'Quais foram os maiores clientes do mês?', Icon: Users, tom: 'success' },
];

const iconBtnSx = (theme, on = false) => ({
  width: 34,
  height: 34,
  flexShrink: 0,
  borderRadius: '10px',
  border: `1px solid ${on ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: on ? 'info.bg' : 'background.paper',
  color: on ? 'primary.main' : 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  transition: '.15s',
  '&:hover': { borderColor: theme.palette.primary.main, color: on ? 'primary.main' : 'text.primary' },
  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
});

function AiBadge({ size = 28 }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size, height: size, borderRadius: size > 30 ? '10px' : '8px', flexShrink: 0, color: '#fff',
        backgroundImage: (t) => t.palette.gradient.brand,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Sparkles size={size > 30 ? 18 : 15} />
    </Box>
  );
}

// ---------- blocos de aviso (erro / sem permissão / cota) ----------
function Aviso({ tipo, titulo, children, acao }) {
  const cfg = {
    err: { bg: 'error.bg', cor: 'error.main', Icon: AlertTriangle, role: 'alert' },
    lock: { bg: 'warning.bg', cor: 'warning.main', Icon: Lock, role: 'alert' },
    quota: { bg: 'secondary.bg', cor: 'secondary.main', Icon: AlertTriangle, role: 'alert' },
  }[tipo];
  const { Icon } = cfg;
  return (
    <Box role={cfg.role} sx={{ display: 'flex', gap: 1.4, alignItems: 'flex-start', p: '12px 14px', borderRadius: '12px', backgroundColor: cfg.bg, flex: 1, minWidth: 0 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: '9px', backgroundColor: 'background.paper', color: cfg.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={16} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="b" sx={{ display: 'block', fontSize: '13.5px', fontWeight: 700, mb: '2px' }}>{titulo}</Typography>
        <Typography component="p" sx={{ m: '0 0 8px', color: 'text.secondary', fontSize: '12.5px', lineHeight: 1.5 }}>{children}</Typography>
        {acao}
      </Box>
    </Box>
  );
}

function BotaoGhost({ onClick, children, testId }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-testid={testId}
      sx={(t) => ({
        display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: '6px', borderRadius: '10px', fontSize: 12, fontWeight: 600,
        fontFamily: 'inherit', cursor: 'pointer', backgroundColor: 'background.paper', color: 'text.primary', border: `1px solid ${t.palette.divider}`,
        '&:hover': { borderColor: t.palette.primary.main, color: 'primary.main' },
        '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
      })}
    >
      {children}
    </Box>
  );
}

function ErroDaMensagem({ erro, onRetry, onNavigate }) {
  if (erro.kind === 'quota') {
    return (
      <Aviso tipo="quota" titulo="Cota mensal de IA esgotada">
        {erro.message || 'A cota mensal de perguntas da empresa foi atingida.'} Você ainda pode navegar normalmente pelo ERP.
      </Aviso>
    );
  }
  if (erro.kind === 'permission') {
    return (
      <Aviso
        tipo="lock"
        titulo="Sem permissão para usar o assistente"
        acao={<BotaoGhost onClick={() => onNavigate('/home')}>Voltar ao início</BotaoGhost>}
      >
        Seu perfil não tem acesso a este recurso. Peça a um administrador para ajustar seu perfil.
      </Aviso>
    );
  }
  const titulo = erro.kind === 'rate' ? 'Muitas perguntas em pouco tempo' : erro.kind === 'session' ? 'Sessão expirada' : 'Não consegui responder agora';
  return (
    <Aviso
      tipo="err"
      titulo={titulo}
      acao={erro.kind === 'session' ? null : (
        <BotaoGhost onClick={onRetry} testId="ai-chat-retry"><RotateCcw size={13} /> Tentar de novo</BotaoGhost>
      )}
    >
      {erro.message} Seus dados não foram alterados.
    </Aviso>
  );
}

// ---------- uma mensagem ----------
function Mensagem({ msg, isLast, busy, canOpen, onNavigate, onAsk, onRetry, onFeedback, shell = false }) {
  const theme = useTheme();
  const [copiado, setCopiado] = useState(false);

  if (msg.role === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} data-testid="ai-msg-user">
        <Box sx={shell ? { maxWidth: '84%', p: '11px 14px', borderRadius: '18px', borderBottomRightRadius: '6px', backgroundImage: theme.palette.gradient.brand, color: '#fff', fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : { maxWidth: '85%', px: '13px', py: '10px', borderRadius: '14px', borderBottomRightRadius: '4px', backgroundImage: theme.palette.gradient.brand, color: '#fff', fontSize: '13.5px', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {msg.text}
        </Box>
      </Box>
    );
  }

  const streaming = msg.status === 'streaming';
  const done = msg.status === 'done';
  const sugestoes = done && isLast ? sugestoesPorTools(msg.tools) : { links: [], asks: [] };
  const linksVisiveis = sugestoes.links.filter(canOpen);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(msg.text);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (e) {
      // sem permissão de clipboard: ignora silenciosamente
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.25, maxWidth: '100%' }} data-testid="ai-msg-assistant">
      {!shell && <AiBadge />}
      <Box sx={shell ? { maxWidth: '84%', minWidth: 0, p: '11px 14px', borderRadius: '18px', borderBottomLeftRadius: '6px', backgroundColor: 'background.paper', border: `1px solid ${theme.palette.divider}`, fontSize: 14, lineHeight: 1.45 } : { flex: 1, minWidth: 0, px: '13px', py: '10px', borderRadius: '14px', borderBottomLeftRadius: '4px', backgroundColor: 'background.subtle', border: `1px solid ${theme.palette.divider}` }}>
        {streaming && !msg.text && (
          msg.toolLabel ? (
            <Box data-testid="ai-tool-status" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.1, fontSize: '12.5px', fontWeight: 600, color: 'text.secondary', px: 1.5, py: 1, borderRadius: '12px', backgroundColor: 'background.paper', border: `1px dashed ${theme.palette.divider}` }}>
              <CircularProgress size={14} thickness={5} />
              <span>{rotuloDaTool(msg.toolLabel)}</span>
            </Box>
          ) : (
            <Box aria-label="Pensando" role="status" sx={{ display: 'inline-flex', gap: '4px', py: '6px' }}>
              {[0, 1, 2].map((n) => (
                <Box key={n} component="i" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'text.disabled', animation: 'aiBounce 1.2s infinite', animationDelay: `${n * 0.15}s`, '@keyframes aiBounce': { '0%,60%,100%': { transform: 'translateY(0)', opacity: 0.5 }, '30%': { transform: 'translateY(-4px)', opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} />
              ))}
            </Box>
          )
        )}

        {done && msg.tools.length > 0 && (
          <Box sx={{ fontSize: '11.5px', color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px', mb: 1 }}>
            <Box component="span" sx={{ color: 'success.main', display: 'flex' }}><Check size={13} /></Box>
            {msg.tools.length === 1 ? '1 consulta realizada' : `${msg.tools.length} consultas realizadas`}
          </Box>
        )}

        {msg.text && <MessageContent text={msg.text} streaming={streaming} onNavigate={onNavigate} canOpen={canOpen} />}

        {msg.status === 'stopped' && (
          <Typography sx={{ mt: msg.text ? 1 : 0, fontSize: '11.5px', color: 'text.disabled' }}>Resposta interrompida.</Typography>
        )}

        {msg.status === 'error' && msg.error && (
          <Box sx={{ mt: msg.text ? 1 : 0 }}>
            <ErroDaMensagem erro={msg.error} onRetry={() => onRetry(msg.id)} onNavigate={onNavigate} />
          </Box>
        )}

        {(linksVisiveis.length > 0 || sugestoes.asks.length > 0) && !busy && (
          <Box data-testid="ai-followups" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
            {linksVisiveis.map((l) => (
              <DeepLinkChip key={l.path} link={l} onNavigate={onNavigate}>{l.label}</DeepLinkChip>
            ))}
            {sugestoes.asks.map((a) => (
              <Box
                key={a}
                component="button"
                type="button"
                onClick={() => onAsk(a)}
                sx={{ display: 'inline-flex', px: '11px', py: '6px', borderRadius: '20px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', border: `1px solid ${theme.palette.divider}`, backgroundColor: 'background.paper', color: 'text.primary', cursor: 'pointer', '&:hover': { borderColor: theme.palette.primary.main, color: 'primary.main' }, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
              >
                {a}
              </Box>
            ))}
          </Box>
        )}

        {done && msg.text && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: 1.25, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', mr: 'auto' }}>
              {msg.feedback ? (msg.feedback === 'up' ? 'Obrigado pelo feedback!' : 'Obrigado. Vamos usar isso para melhorar.') : 'Esta resposta ajudou?'}
            </Typography>
            <FbBtn label="Resposta útil" ativo={msg.feedback === 'up'} cor="success" onClick={() => onFeedback(msg.id, 'up')} testId="ai-fb-up"><ThumbsUp size={14} /></FbBtn>
            <FbBtn label="Resposta ruim" ativo={msg.feedback === 'down'} cor="error" onClick={() => onFeedback(msg.id, 'down')} testId="ai-fb-down"><ThumbsDown size={14} /></FbBtn>
            <FbBtn label={copiado ? 'Resposta copiada' : 'Copiar resposta'} onClick={copiar}>{copiado ? <Check size={14} /> : <Copy size={14} />}</FbBtn>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function FbBtn({ label, ativo = false, cor, onClick, children, testId }) {
  const theme = useTheme();
  const aplicaCor = ativo && cor;
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      aria-pressed={cor ? ativo : undefined}
      title={label}
      data-testid={testId}
      onClick={onClick}
      sx={{
        width: 30, height: 28, borderRadius: '8px', border: '1px solid transparent', fontFamily: 'inherit', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '.15s',
        backgroundColor: aplicaCor ? `${cor}.bg` : 'transparent',
        color: aplicaCor ? `${cor}.main` : 'text.disabled',
        '&:hover': { backgroundColor: aplicaCor ? `${cor}.bg` : 'background.paper', borderColor: theme.palette.divider, color: aplicaCor ? `${cor}.main` : 'text.primary' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      }}
    >
      {children}
    </Box>
  );
}

// ---------- estado vazio ----------
function EstadoVazio({ nome, onAsk, disabled }) {
  const theme = useTheme();
  return (
    <Box data-testid="ai-empty" sx={{ m: 'auto', textAlign: 'center', width: '100%', py: 1.5 }}>
      <Box sx={{ width: 56, height: 56, borderRadius: '16px', mx: 'auto', mb: 1.75, color: '#fff', backgroundImage: theme.palette.gradient.brand, boxShadow: '0 8px 22px rgba(37,99,235,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Sparkles size={26} />
      </Box>
      <Typography component="h3" sx={{ m: '0 0 4px', fontSize: 18, fontWeight: 800, letterSpacing: '-.02em' }}>
        {nome ? `Olá, ${nome}! Como posso ajudar?` : 'Olá! Como posso ajudar?'}
      </Typography>
      <Typography component="p" sx={{ m: '0 0 18px', color: 'text.secondary', fontSize: 13 }}>
        Pergunte sobre vendas, estoque e clientes. Eu só consulto, nunca altero dados.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'left' }}>
        {SUGESTOES.map(({ id, titulo, detalhe, pergunta, Icon, tom }) => (
          <Box
            key={id}
            component="button"
            type="button"
            disabled={disabled}
            data-testid={`ai-suggestion-${id}`}
            onClick={() => onAsk(pergunta)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, p: '11px 13px', width: '100%', textAlign: 'left', borderRadius: '12px', fontFamily: 'inherit',
              border: `1px solid ${theme.palette.divider}`, backgroundColor: 'background.paper', color: 'text.primary', cursor: 'pointer', transition: '.15s',
              '&:hover': { borderColor: theme.palette.primary.main, boxShadow: theme.customShadows?.md },
              '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
              '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: '9px', backgroundColor: `${tom}.bg`, color: `${tom}.main`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={16} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" sx={{ display: 'block', fontSize: '13.5px', fontWeight: 600 }}>{titulo}</Typography>
              <Typography component="small" sx={{ display: 'block', fontSize: '11.5px', color: 'text.disabled' }}>{detalhe}</Typography>
            </Box>
            <Box component="span" sx={{ ml: 'auto', color: 'text.disabled', display: 'flex' }}><ChevronRight size={16} /></Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---------- histórico ----------
function rotuloGrupo(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Mais antigas';
  const hoje = new Date();
  const dia = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dif = Math.round((dia(hoje) - dia(d)) / 86400000);
  if (dif <= 0) return 'Hoje';
  if (dif === 1) return 'Ontem';
  if (dif <= 7) return 'Últimos 7 dias';
  return 'Mais antigas';
}

function formatarQuando(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return rotuloGrupo(iso) === 'Hoje' ? hora : `${data} · ${hora}`;
}

function Historico({ currentId, onOpen, onNew, onDeleted }) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery(['ai-conversations'], () => listConversations(), {
    refetchOnWindowFocus: false,
    retry: false,
  });

  const excluir = useMutation((id) => deleteConversation(id), {
    onSuccess: (_, id) => {
      setConfirmando(null);
      queryClient.invalidateQueries(['ai-conversations']);
      onDeleted(id);
    },
  });

  const conversas = data || [];
  const grupos = [];
  conversas.forEach((c) => {
    const rotulo = rotuloGrupo(c.atualizada_em);
    let g = grupos.find((x) => x.rotulo === rotulo);
    if (!g) { g = { rotulo, itens: [] }; grupos.push(g); }
    g.itens.push(c);
  });

  return (
    <Box data-testid="ai-history" sx={{ flex: 1, overflowY: 'auto', p: '14px 16px', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box
        component="button"
        type="button"
        onClick={onNew}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, p: '9px 14px', borderRadius: '10px', border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', backgroundImage: theme.palette.gradient.brand, '&:hover': { filter: 'brightness(1.06)' }, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
      >
        <Plus size={14} /> Nova conversa
      </Box>

      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>}

      {isError && (
        <Box sx={{ mt: 1.5 }}>
          <Aviso tipo="err" titulo="Não consegui carregar o histórico" acao={<BotaoGhost onClick={() => refetch()}><RotateCcw size={13} /> Tentar de novo</BotaoGhost>}>
            Tente novamente em instantes.
          </Aviso>
        </Box>
      )}

      {!isLoading && !isError && conversas.length === 0 && (
        <Typography sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 13, py: 4 }}>Nenhuma conversa ainda.</Typography>
      )}

      {grupos.map((g) => (
        <React.Fragment key={g.rotulo}>
          <Typography sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'text.disabled', m: '10px 4px 4px' }}>{g.rotulo}</Typography>
          {g.itens.map((c) => {
            const atual = c.id === currentId;
            return (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: '12px', border: `1px solid ${atual ? 'transparent' : theme.palette.divider}`, backgroundColor: atual ? 'info.bg' : 'background.paper', '&:hover': { borderColor: theme.palette.primary.main } }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => onOpen(c.id)}
                  data-testid="ai-history-item"
                  sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25, p: '10px 12px', background: 'none', border: 'none', textAlign: 'left', fontFamily: 'inherit', color: 'text.primary', cursor: 'pointer', borderRadius: '12px', '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography component="b" noWrap sx={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{c.titulo || 'Conversa'}</Typography>
                    <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{formatarQuando(c.atualizada_em)}</Typography>
                  </Box>
                </Box>
                {confirmando === c.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1, fontSize: 12 }}>
                    <Box component="button" type="button" disabled={excluir.isLoading} onClick={() => excluir.mutate(c.id)} sx={{ border: 'none', background: 'none', color: 'error.main', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}>Excluir</Box>
                    <Box component="button" type="button" onClick={() => setConfirmando(null)} sx={{ border: 'none', background: 'none', color: 'text.secondary', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}>Cancelar</Box>
                  </Box>
                ) : (
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Excluir conversa ${c.titulo || ''}`.trim()}
                    title="Excluir"
                    onClick={() => setConfirmando(c.id)}
                    sx={{ mr: 0.75, p: '6px', border: 'none', background: 'none', borderRadius: '6px', color: 'text.disabled', cursor: 'pointer', display: 'flex', '&:hover': { color: 'error.main', backgroundColor: 'error.bg' }, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}` } }}
                  >
                    <Trash2 size={14} />
                  </Box>
                )}
              </Box>
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );
}

// ---------- conversa (compartilhada entre o painel lateral e a página /chat) ----------
// Renderiza cabeçalho + mensagens/histórico + campo como filhos de um contêiner
// flex em coluna fornecido por quem usa. `variant`: 'drawer' (painel lateral,
// com botão fechar e Esc) | 'page' (tela cheia, conteúdo centralizado).
export function AiChatConversation({ open = true, onClose, variant = 'drawer' }) {
  const isPage = variant === 'page';
  // Página dentro do shell mobile: segue `prototipos-mobile/principal/assistente.html`.
  const isMobileShell = useIsMobileShell();
  const shell = isPage && isMobileShell;
  const [newOpen, setNewOpen] = useState(false);
  // Em página, o conteúdo fica centralizado com largura máxima de leitura.
  const gutter = (vertical, horizontal) => (isPage ? `${vertical} max(16px, calc((100% - 860px) / 2))` : `${vertical} ${horizontal}`);
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission, hasAnyPermission, userData } = usePermissions();
  const chat = useAiChat();
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const [texto, setTexto] = useState('');
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const colado = useRef(true);

  const nome = (userData?.name || '').trim().split(/\s+/)[0];
  const canOpen = useCallback(
    (link) => (Array.isArray(link.permission) ? hasAnyPermission(link.permission) : hasPermission(link.permission)),
    [hasPermission, hasAnyPermission],
  );

  const irPara = useCallback((path) => {
    navigate(path);
    if (isMobile && !isPage && onClose) onClose();
  }, [navigate, isMobile, isPage, onClose]);

  // Foco no campo ao abrir; Esc fecha (só no painel lateral).
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 300);
    const onKey = (e) => { if (e.key === 'Escape' && !isPage && onClose) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); };
  }, [open, isPage, onClose]);

  // Rolagem automática enquanto o usuário não rolou para cima.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && colado.current) el.scrollTop = el.scrollHeight;
  }, [chat.messages, view]);

  // Conversa nova/atualizada muda a lista do histórico.
  useEffect(() => {
    if (!chat.busy) queryClient.invalidateQueries(['ai-conversations']);
  }, [chat.busy, chat.conversationId, queryClient]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el) colado.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const enviar = (t) => {
    const msg = String(t ?? texto).trim();
    if (!msg || chat.busy || chat.quotaExceeded) return;
    setView('chat');
    colado.current = true;
    setTexto('');
    chat.send(msg);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  const abrirConversa = async (id) => {
    colado.current = true;
    if (await chat.openConversation(id)) setView('chat');
  };

  const novaConversa = () => {
    chat.newConversation();
    setView('chat');
    setTexto('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const alternarHistorico = () => {
    if (view === 'history') { setView('chat'); return; }
    queryClient.invalidateQueries(['ai-conversations']);
    setView('history');
  };

  useAppBarAction({ label: 'Nova conversa', icon: RefreshIcon, onClick: () => setNewOpen(true), enabled: shell });
  useAppBarSubtitle(shell ? 'Pergunte em linguagem natural' : null);

  const vazio = chat.messages.length === 0;
  const podeEnviar = texto.trim().length > 0 && !chat.quotaExceeded;
  const lastAssistantId = [...chat.messages].reverse().find((m) => m.role === 'assistant')?.id;

  return (
    <>
      {/* Cabeçalho (no shell mobile o título/subtítulo/ação ficam na barra superior) */}
      {!shell && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: gutter('14px', '16px'), borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        {!isPage && (
          <Box component="button" type="button" onClick={onClose} aria-label="Fechar assistente" title="Fechar" data-testid="ai-chat-close" sx={iconBtnSx(theme)}><X size={16} /></Box>
        )}
        <AiBadge size={34} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 800, letterSpacing: '-.01em' }}>Assistente FlowVendas</Typography>
          <Box sx={{ fontSize: '11.5px', color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box component="i" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'success.main', display: 'inline-block' }} />
            Somente consulta · dados da sua empresa
          </Box>
        </Box>
        <Box component="button" type="button" onClick={alternarHistorico} aria-label="Histórico de conversas" title="Histórico" aria-pressed={view === 'history'} data-testid="ai-chat-history-btn" sx={iconBtnSx(theme, view === 'history')}><History size={16} /></Box>
        <Box component="button" type="button" onClick={novaConversa} aria-label="Nova conversa" title="Nova conversa" data-testid="ai-chat-new" sx={iconBtnSx(theme)}><Plus size={16} /></Box>
      </Box>}

      {view === 'history' ? (
        <Historico currentId={chat.conversationId} onOpen={abrirConversa} onNew={novaConversa} onDeleted={chat.onConversationDeleted} />
      ) : (
        <Box
          ref={scrollRef}
          onScroll={onScroll}
          role="log"
          aria-live="polite"
          aria-busy={chat.busy}
          data-testid="ai-chat-messages"
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', p: shell ? '16px 16px 28px' : isPage ? gutter('18px', '') : (isMobile ? '14px 12px' : '18px 16px'), display: 'flex', flexDirection: 'column', gap: shell ? '14px' : 2 }}
        >
          {chat.loadingConversation && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>}

          {chat.loadError && !chat.loadingConversation && (
            <Aviso tipo="err" titulo="Não consegui abrir a conversa">{chat.loadError}</Aviso>
          )}

          {!chat.loadingConversation && vazio && !chat.loadError && (
            <EstadoVazio nome={nome} onAsk={enviar} disabled={chat.quotaExceeded} />
          )}

          {!chat.loadingConversation && chat.messages.map((m) => (
            <Mensagem
              key={m.id}
              msg={m}
              isLast={m.id === lastAssistantId}
              busy={chat.busy}
              canOpen={canOpen}
              onNavigate={irPara}
              onAsk={enviar}
              onRetry={chat.retry}
              onFeedback={chat.setFeedback}
              shell={shell}
            />
          ))}
        </Box>
      )}

      {/* Rodapé / campo — shell mobile: `.formbar` com chips + busca + enviar */}
      {shell && view === 'chat' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '10px 16px calc(12px + env(safe-area-inset-bottom, 0px))', backgroundColor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}`, boxShadow: '0 -8px 24px rgba(15,23,42,.05)', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: '8px', overflowX: 'auto', p: '2px 16px 4px', m: '0 -16px' }}>
            {SUGESTOES.map(({ id, titulo, pergunta }) => (
              <Box key={id} component="button" type="button" disabled={chat.busy || chat.quotaExceeded} onClick={() => enviar(pergunta)} sx={{ flexShrink: 0, height: 36, p: '0 14px', borderRadius: '18px', backgroundColor: 'background.paper', border: `1px solid ${theme.palette.divider}`, color: 'text.secondary', fontWeight: 600, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', cursor: 'pointer', '&:disabled': { opacity: 0.6 } }}>
                {titulo}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box component="label" sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px', height: 46, borderRadius: '14px', backgroundColor: 'background.paper', border: `1px solid ${theme.palette.divider}`, p: '0 14px', color: 'text.disabled', opacity: chat.quotaExceeded ? 0.6 : 1 }}>
              <ProtoIcon name="msg" size={20} />
              <InputBase
                fullWidth
                inputRef={inputRef}
                value={texto}
                disabled={chat.quotaExceeded}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={chat.quotaExceeded ? 'Cota mensal esgotada' : 'Pergunte algo…'}
                inputProps={{ 'aria-label': 'Mensagem', maxLength: MAX_MENSAGEM, 'data-testid': 'ai-chat-input' }}
                sx={{ fontSize: 16, color: 'text.primary', '& input::placeholder': { color: 'text.disabled', opacity: 1 } }}
              />
            </Box>
            {chat.busy ? (
              <Box component="button" type="button" onClick={chat.stop} aria-label="Parar resposta" data-testid="ai-chat-stop" sx={{ width: 48, height: 48, borderRadius: '14px', border: 0, cursor: 'pointer', color: 'background.default', backgroundColor: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Square size={16} fill="currentColor" />
              </Box>
            ) : (
              <Box component="button" type="button" onClick={() => enviar()} disabled={!podeEnviar} aria-label="Enviar" data-testid="ai-chat-send" sx={{ width: 48, height: 48, p: 0, borderRadius: '14px', border: 0, cursor: 'pointer', color: '#fff', backgroundImage: theme.palette.gradient.brand, boxShadow: '0 6px 16px rgba(37,99,235,.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&:disabled': { backgroundImage: 'none', boxShadow: 'none', backgroundColor: theme.palette.divider, color: theme.palette.text.disabled, cursor: 'not-allowed' } }}>
                <ProtoIcon name="send" size={20} />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {shell && (
        <MobileSheet
          open={newOpen}
          onClose={() => setNewOpen(false)}
          title="Nova conversa"
          footer={(
            <>
              <KitButton sx={{ flex: 1 }} onClick={() => setNewOpen(false)}>Cancelar</KitButton>
              <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => { setNewOpen(false); novaConversa(); }}>Iniciar nova</KitButton>
            </>
          )}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: 12.5, mb: '12px' }}>A conversa atual será arquivada.</Typography>
          <KitItem icon={HistoryIcon} tone="gray" title="Histórico de conversas" chevron onClick={() => { setNewOpen(false); alternarHistorico(); }} sx={{ border: 0, p: '12px 4px' }} />
        </MobileSheet>
      )}

      {!shell && view === 'chat' && (
        <Box sx={{ p: isPage ? gutter('12px', '') : (isMobile ? '10px 12px calc(12px + env(safe-area-inset-bottom))' : '12px 16px 14px'), borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0, backgroundColor: 'background.paper' }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'flex-end', gap: 1, p: '6px 6px 6px 14px', borderRadius: '14px',
              border: `1px solid ${theme.palette.divider}`, backgroundColor: 'background.subtle', transition: '.15s',
              opacity: chat.quotaExceeded ? 0.6 : 1,
              '&:focus-within': { borderColor: theme.palette.primary.main, boxShadow: '0 0 0 3px rgba(37,99,235,.12)', backgroundColor: 'background.paper' },
            }}
          >
            <InputBase
              multiline
              maxRows={5}
              fullWidth
              inputRef={inputRef}
              value={texto}
              disabled={chat.quotaExceeded}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={chat.quotaExceeded ? 'Cota mensal esgotada' : 'Pergunte sobre vendas, estoque, clientes…'}
              inputProps={{ 'aria-label': 'Mensagem', maxLength: MAX_MENSAGEM, 'data-testid': 'ai-chat-input' }}
              sx={{ fontSize: '13.5px', py: '4px', lineHeight: 1.4 }}
            />
            {chat.busy ? (
              <Box component="button" type="button" onClick={chat.stop} aria-label="Parar resposta" data-testid="ai-chat-stop" sx={{ width: 36, height: 36, borderRadius: '10px', border: 'none', cursor: 'pointer', color: 'background.default', backgroundColor: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Square size={14} fill="currentColor" />
              </Box>
            ) : (
              <Box
                component="button"
                type="button"
                onClick={() => enviar()}
                disabled={!podeEnviar}
                aria-label="Enviar"
                data-testid="ai-chat-send"
                sx={{ width: 36, height: 36, borderRadius: '10px', border: 'none', cursor: 'pointer', color: '#fff', backgroundImage: theme.palette.gradient.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&:disabled': { backgroundImage: 'none', backgroundColor: theme.palette.divider, color: theme.palette.text.disabled, cursor: 'not-allowed' }, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
              >
                <ArrowUp size={16} strokeWidth={2.4} />
              </Box>
            )}
          </Box>
          <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', textAlign: 'center', mt: 1 }}>
            O assistente só consulta dados e pode errar. Confira valores importantes na tela do ERP.
          </Typography>
        </Box>
      )}
    </>
  );
}

// ---------- painel lateral ----------
function PainelConteudo({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  return (
    <Box
      component="aside"
      role="dialog"
      aria-label="Assistente de IA"
      aria-modal="false"
      aria-hidden={!open}
      data-testid="ai-chat-panel"
      sx={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: isMobile ? '100vw' : 440, maxWidth: '100vw',
        zIndex: theme.zIndex.drawer + 2,
        backgroundColor: 'background.paper',
        borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        boxShadow: theme.customShadows?.lg,
        display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(105%)',
        visibility: open ? 'visible' : 'hidden',
        transition: open ? 'transform .28s ease, visibility 0s' : 'transform .28s ease, visibility 0s linear .28s',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <AiChatConversation open={open} onClose={onClose} variant="drawer" />
    </Box>
  );
}

/**
 * Painel do assistente de IA (lateral no desktop, tela cheia no mobile).
 * Montado no shell (`Main.js`) só quando o recurso está habilitado. O conteúdo
 * (e as chamadas ao /me via `usePermissions`) só monta na primeira abertura e
 * depois permanece montado para preservar a conversa ao fechar/reabrir.
 */
export default function AiChatPanel() {
  const [openState, setOpen] = useRecoilState(aiChatOpenState);
  const [jaAbriu, setJaAbriu] = useState(false);
  const location = useLocation();
  // Na página /chat (tela cheia) o painel lateral fica fechado: evita duas
  // telas do assistente (e dois streams SSE) ao mesmo tempo.
  const naPaginaChat = location.pathname === '/chat';
  const open = openState && !naPaginaChat;

  useEffect(() => { if (open) setJaAbriu(true); }, [open]);
  useEffect(() => { if (naPaginaChat) setOpen(false); }, [naPaginaChat, setOpen]);

  const fechar = useCallback(() => setOpen(false), [setOpen]);

  if (!open && !jaAbriu) return null;
  return <PainelConteudo open={open} onClose={fechar} />;
}
